import { useEffect, useRef, useState, useCallback } from "react";
import { Play, RotateCcw, RotateCw, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

const COLS = 10, ROWS = 20, CELL = 22;
const W = COLS * CELL, H = ROWS * CELL;

const SHAPES = {
  I: { c: "#00F0FF", m: [[1, 1, 1, 1]] },
  O: { c: "#F5C842", m: [[1, 1], [1, 1]] },
  T: { c: "#a855f7", m: [[0, 1, 0], [1, 1, 1]] },
  S: { c: "#22c55e", m: [[0, 1, 1], [1, 1, 0]] },
  Z: { c: "#FF006E", m: [[1, 1, 0], [0, 1, 1]] },
  L: { c: "#f97316", m: [[0, 0, 1], [1, 1, 1]] },
  J: { c: "#3b82f6", m: [[1, 0, 0], [1, 1, 1]] },
};
const KEYS = Object.keys(SHAPES);

const emptyGrid = () => Array.from({ length: ROWS }, () => Array(COLS).fill(null));
const rotate = (m) => m[0].map((_, i) => m.map((row) => row[i]).reverse());

export default function TetrisGame({ onGameOver }) {
  const canvasRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    if (gameOver && onGameOver) onGameOver(score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameOver]);
  const grid = useRef(emptyGrid());
  const piece = useRef(null);

  const spawn = useCallback(() => {
    const k = KEYS[Math.floor(Math.random() * KEYS.length)];
    const s = SHAPES[k];
    piece.current = { m: s.m, c: s.c, x: Math.floor((COLS - s.m[0].length) / 2), y: 0 };
    return valid(piece.current.m, piece.current.x, piece.current.y);
  }, []);

  const valid = (m, px, py) => {
    for (let r = 0; r < m.length; r++)
      for (let c = 0; c < m[r].length; c++) {
        if (!m[r][c]) continue;
        const x = px + c, y = py + r;
        if (x < 0 || x >= COLS || y >= ROWS) return false;
        if (y >= 0 && grid.current[y][x]) return false;
      }
    return true;
  };

  const merge = () => {
    const p = piece.current;
    p.m.forEach((row, r) => row.forEach((v, c) => {
      if (v && p.y + r >= 0) grid.current[p.y + r][p.x + c] = p.c;
    }));
    let cleared = 0;
    grid.current = grid.current.filter((row) => {
      if (row.every((cell) => cell)) { cleared++; return false; }
      return true;
    });
    while (grid.current.length < ROWS) grid.current.unshift(Array(COLS).fill(null));
    if (cleared) setScore((s) => s + cleared * 100);
  };

  const start = () => {
    grid.current = emptyGrid();
    setScore(0);
    setGameOver(false);
    spawn();
    setRunning(true);
  };

  const draw = useCallback(() => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#0A0A14"; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(0,240,255,0.05)";
    for (let i = 0; i <= COLS; i++) { ctx.beginPath(); ctx.moveTo(i * CELL, 0); ctx.lineTo(i * CELL, H); ctx.stroke(); }
    for (let i = 0; i <= ROWS; i++) { ctx.beginPath(); ctx.moveTo(0, i * CELL); ctx.lineTo(W, i * CELL); ctx.stroke(); }
    const cell = (x, y, color) => { ctx.fillStyle = color; ctx.fillRect(x * CELL + 1, y * CELL + 1, CELL - 2, CELL - 2); };
    grid.current.forEach((row, y) => row.forEach((v, x) => { if (v) cell(x, y, v); }));
    const p = piece.current;
    if (p) p.m.forEach((row, r) => row.forEach((v, c) => { if (v) cell(p.x + c, p.y + r, p.c); }));
  }, []);

  const doMove = (action) => {
    if (!running || !piece.current) return;
    const p = piece.current;
    if (action === "left" && valid(p.m, p.x - 1, p.y)) p.x--;
    else if (action === "right" && valid(p.m, p.x + 1, p.y)) p.x++;
    else if (action === "down" && valid(p.m, p.x, p.y + 1)) p.y++;
    else if (action === "rotate") { const rm = rotate(p.m); if (valid(rm, p.x, p.y)) p.m = rm; }
    draw();
  };

  useEffect(() => {
    const key = (e) => {
      if (!running || !piece.current) return;
      if (e.key === "ArrowLeft") doMove("left");
      else if (e.key === "ArrowRight") doMove("right");
      else if (e.key === "ArrowDown") doMove("down");
      else if (e.key === "ArrowUp") doMove("rotate");
      if (e.key.startsWith("Arrow")) e.preventDefault();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, draw]);

  useEffect(() => {
    if (!running) return;
    draw();
    const id = setInterval(() => {
      const p = piece.current;
      if (valid(p.m, p.x, p.y + 1)) {
        p.y++;
      } else {
        merge();
        if (!spawn()) { setRunning(false); setGameOver(true); return; }
      }
      draw();
    }, 550);
    return () => clearInterval(id);
  }, [running, draw, spawn]);

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center justify-between w-full max-w-[220px] mb-4">
        <span className="font-arcade text-2xl text-[#F5C842]" data-testid="tetris-score">{score}</span>
        <span className="font-arcade text-sm text-slate-500">ARROWS · ↑ ROTATE</span>
      </div>
      <div className="relative scanlines border border-cyan-500/30">
        <canvas ref={canvasRef} width={W} height={H} className="block max-w-full" data-testid="tetris-canvas" />
        {!running && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#0A0A14]/85 gap-4">
            {gameOver && <p className="font-arcade text-3xl text-[#FF006E]">GAME OVER</p>}
            <button onClick={start} data-testid="tetris-start-btn" className="font-arcade text-xl px-6 py-3 bg-[#00F0FF] text-black border border-[#00F0FF] hover:shadow-[4px_4px_0px_#FF006E] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform duration-200 inline-flex items-center gap-2">
              {gameOver ? <><RotateCcw className="w-5 h-5" /> RETRY</> : <><Play className="w-5 h-5" /> START</>}
            </button>
          </div>
        )}
      </div>

      {/* Mobile controls */}
      <div className="mt-6 md:hidden select-none" data-testid="tetris-controls">
        <div className="flex justify-center mb-3">
          <TBtn testid="tetris-rotate" onPress={() => doMove("rotate")}><RotateCw className="w-6 h-6" /></TBtn>
        </div>
        <div className="flex justify-center gap-3">
          <TBtn testid="tetris-left" onPress={() => doMove("left")}><ChevronLeft className="w-6 h-6" /></TBtn>
          <TBtn testid="tetris-down" onPress={() => doMove("down")}><ChevronDown className="w-6 h-6" /></TBtn>
          <TBtn testid="tetris-right" onPress={() => doMove("right")}><ChevronRight className="w-6 h-6" /></TBtn>
        </div>
      </div>
    </div>
  );
}

function TBtn({ children, onPress, testid }) {
  const fire = (e) => { e.preventDefault(); onPress(); };
  return (
    <button
      data-testid={testid}
      onTouchStart={fire} onMouseDown={fire}
      className="w-16 h-14 flex items-center justify-center bg-[#11111A] border border-[#00F0FF]/50 text-[#00F0FF] active:bg-[#00F0FF] active:text-black transition-colors duration-100 touch-manipulation"
    >
      {children}
    </button>
  );
}
